import type { Address } from '@vipso/shared';
import { baseApi } from '../store/baseApi';

export interface TenantBySlugResult {
  tenantBySlug: {
    tenant: {
      id: string;
      name: string;
      slug: string;
      plan: string;
      status: string;
    };
    theme: {
      primaryColor?: string | null;
      secondaryColor?: string | null;
      logoUrl?: string | null;
      fontFamily?: string | null;
      mode?: string | null;
    } | null;
    config: {
      defaultContextLogin: boolean;
      loginType: string;
      logoFileId?: string | null;
      logoUrl?: string | null;
      maxOrgs?: number | null;
      maxUsers?: number | null;
      enabledModules?: string[] | null;
    } | null;
    orgs: Array<{
      id: string;
      name: string;
      uniqueId?: string | null;
      status?: string | null;
    }>;
  };
}

export interface Customer {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  address?: Address | null;
  openingBalance?: number | null;
  approvalStatus?: string | null;
  approvedById?: string | null;
  approvedAt?: string | null;
}

export interface Vendor {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  openingBalance?: number | null;
  address?: Address | null;
  approvalStatus?: string | null;
  approvedById?: string | null;
  approvedAt?: string | null;
}

export interface ProductImage {
  id: string;
  fileId: string;
  url?: string | null;
  sortOrder: number;
  isMain: boolean;
}

export interface Product {
  id: string;
  name: string;
  unit?: string | null;
  categoryId?: string | null;
  category?: { id: string; name: string } | null;
  description?: string | null;
  quantity?: number | null;
  pricePerQuantity?: number | null;
  showSubse?: boolean | null;
  addOnItem?: boolean | null;
  addOnItemName?: string | null;
  imageFileId?: string | null;
  imageUrl?: string | null;
  images?: ProductImage[] | null;
  approvalStatus?: string | null;
  approvedById?: string | null;
  approvedAt?: string | null;
}

const PRODUCT_SELECTION = `
  id name unit categoryId description quantity pricePerQuantity
  showSubse addOnItem addOnItemName imageFileId imageUrl
  approvalStatus approvedById approvedAt
  category { id name }
`;

const PRODUCT_DETAIL_SELECTION = `
  ${PRODUCT_SELECTION}
  images { id fileId url sortOrder isMain }
`;

const ADDRESS_SELECTION = `address { id line1 line2 city state postalCode country }`;
const PAGINATION_FIELDS = `total page pageLimit`;

const PROC_SELECTION = `
  id procType ordNumber procAmount procUrl customerId vendorId warehouseId createdAt
  tax taxType taxAmount discount discountType discountAmount
  chargesType chargesDirection chargesAmount remarks vehicleNo biltyNo
  receiptNumber gatePassNumber loadNumber bagNumber approvalStatus approvedById approvedAt
  approvedBy
  customer { id name phone ${ADDRESS_SELECTION} }
  vendor { id name phone ${ADDRESS_SELECTION} }
  labour { id name phone }
  transport { id name phone }
  products {
    id quantity productId pricePerUnit productPrice qtyUnit addOnItemQty
    chamberId floorId rackId lossPercent lossKg
    chamber { id name }
    floor { id name }
    rack { id name }
    product { id name unit imageFileId imageUrl showSubse addOnItem addOnItemName }
  }
`;

export interface CustomerListResult {
  listCustomers: {
    rows: Customer[];
    total: number;
    page: number;
    pageLimit: number;
  };
}

export interface ProcProductLine {
  id: string;
  quantity?: number | null;
  productId?: string | null;
  product?: {
    id: string;
    name: string;
    unit?: string | null;
    imageFileId?: string | null;
    imageUrl?: string | null;
    showSubse?: boolean | null;
    addOnItem?: boolean | null;
    addOnItemName?: string | null;
  } | null;
  pricePerUnit?: number | null;
  productPrice?: number | null;
  qtyUnit?: string | null;
  addOnItemQty?: number | null;
  chamberId?: string | null;
  floorId?: string | null;
  rackId?: string | null;
  chamber?: { id: string; name: string } | null;
  floor?: { id: string; name: string } | null;
  rack?: { id: string; name: string } | null;
  lossPercent?: number | null;
  lossKg?: number | null;
}

export interface ProcRow {
  id: string;
  procType: string;
  ordNumber?: string | null;
  procAmount?: number | null;
  procUrl?: string | null;
  customerId?: string | null;
  vendorId?: string | null;
  warehouseId?: string | null;
  createdAt?: string | null;
  tax?: number | null;
  taxType?: string | null;
  taxAmount?: number | null;
  discount?: number | null;
  discountType?: string | null;
  discountAmount?: number | null;
  chargesType?: string | null;
  chargesDirection?: string | null;
  chargesAmount?: number | null;
  remarks?: string | null;
  vehicleNo?: string | null;
  biltyNo?: string | null;
  receiptNumber?: string | null;
  gatePassNumber?: string | null;
  loadNumber?: string | null;
  bagNumber?: string | null;
  approvalStatus?: string | null;
  approvedById?: string | null;
  approvedAt?: string | null;
  approvedBy?: { id?: string; name?: string } | null;
  customer?: {
    id: string;
    name: string;
    phone?: string | null;
    address?: Address | null;
  } | null;
  vendor?: {
    id: string;
    name: string;
    phone?: string | null;
    address?: Address | null;
  } | null;
  labour?: { id: string; name: string; phone?: string | null } | null;
  transport?: { id: string; name: string; phone?: string | null } | null;
  products?: ProcProductLine[] | null;
}

export interface ProcListResult {
  listProcurement: {
    rows: ProcRow[];
    total: number;
    page: number;
    pageLimit: number;
  };
}

export interface ModuleComment {
  id: string;
  type: string;
  content: string;
  createdAt: string;
  createdByUser?: Record<string, unknown> | null;
}

export interface InventoryTransferRow {
  id: string;
  orgId: string;
  productId: string;
  fromType: 'PURCHASE_POOL' | 'RACK';
  fromRackId?: string | null;
  toType: 'RACK' | 'PURCHASE_POOL';
  toRackId?: string | null;
  warehouseId?: string | null;
  quantity: number;
  lossPercent?: number | null;
  lossKg?: number | null;
  notes?: string | null;
  status: 'DRAFT' | 'CONFIRMED';
  createdAt?: string;
  product?: { id: string; name: string; unit?: string | null } | null;
  fromRack?: { id: string; name: string } | null;
  toRack?: { id: string; name: string } | null;
}

export interface DashboardStats {
  customers: number;
  vendors: number;
  products: number;
  salesOrders: number;
  purchaseOrders: number;
  totalProcurement: number;
}

export const tenantApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    tenantBySlug: build.query<TenantBySlugResult, string>({
      query: (slug) => ({
        document: `
          query TenantBySlug($slug: String!) {
            tenantBySlug(slug: $slug) {
              tenant { id name slug plan status }
              theme { primaryColor secondaryColor logoUrl fontFamily mode }
              config {
                defaultContextLogin
                loginType
                logoFileId
                logoUrl
                maxOrgs
                maxUsers
                enabledModules
              }
              orgs { id name uniqueId status }
            }
          }
        `,
        variables: { slug },
      }),
      providesTags: ['Tenant'],
    }),
    login: build.mutation<
      Record<string, unknown>,
      { phone: string; tenantId?: string | null }
    >({
      query: ({ phone, tenantId }) => ({
        document: `
          mutation Login($phone: String!, $tenantId: ID) {
            login(phone: $phone, tenantId: $tenantId)
          }
        `,
        variables: { phone, tenantId: tenantId ?? null },
      }),
    }),
    loginWithPassword: build.mutation<
      {
        loginWithPassword: {
          user: { id: string; name: string; phone: string; email?: string | null };
          membershipId: string;
          orgId: string;
          orgName?: string | null;
        };
      },
      { phone: string; password: string; orgId?: string; tenantId?: string | null }
    >({
      query: ({ phone, password, orgId, tenantId }) => ({
        document: `
          mutation LoginWithPassword($phone: String!, $password: String!, $orgId: ID, $tenantId: ID) {
            loginWithPassword(phone: $phone, password: $password, orgId: $orgId, tenantId: $tenantId)
          }
        `,
        variables: { phone, password, orgId, tenantId: tenantId ?? null },
      }),
    }),
    verifyOtp: build.mutation<
      {
        verifyOtp: {
          user: { id: string; name: string; phone: string; email?: string | null };
          orgs: { orgId: string; name?: string | null; membershipId: string }[];
        };
      },
      { phone: string; otp: string; tenantId?: string | null }
    >({
      query: ({ phone, otp, tenantId }) => ({
        document: `
          mutation VerifyOtp($phone: String!, $otp: String!, $tenantId: ID) {
            verifyOtp(phone: $phone, otp: $otp, tenantId: $tenantId) {
              user { id name phone email }
              orgs { orgId name membershipId }
            }
          }
        `,
        variables: { phone, otp, tenantId: tenantId ?? null },
      }),
    }),
    initApp: build.mutation<
      {
        initApp: {
          accessToken: string;
          refreshToken: string;
          featureKeys: string[];
          orgFeatures: Array<{
            featureKey: string;
            displayName?: string | null;
            isEnabled: boolean;
          }>;
          socketRooms: string[];
          org?: Record<string, unknown> | null;
          tenant?: Record<string, unknown> | null;
          theme?: Record<string, unknown> | null;
          currentRole?: Record<string, unknown> | null;
          user?: Record<string, unknown> | null;
          orgs: { orgId: string; name?: string | null; membershipId: string }[];
          pendingTaskCount: number;
          operationsLocked: boolean;
        };
      },
      { userId: string; orgId: string }
    >({
      query: ({ userId, orgId }) => ({
        document: `
          mutation InitApp($userId: ID!, $orgId: ID!) {
            initApp(userId: $userId, orgId: $orgId) {
              accessToken
              refreshToken
              featureKeys
              orgFeatures { featureKey displayName isEnabled }
              socketRooms
              org
              tenant
              theme
              currentRole
              user
              orgs {
                orgId
                name
                membershipId
              }
              pendingTaskCount
              operationsLocked
            }
          }
        `,
        variables: { userId, orgId },
      }),
    }),
    refreshSession: build.mutation<
      {
        refreshSession: {
          accessToken: string;
          refreshToken: string;
          featureKeys: string[];
          orgFeatures: Array<{
            featureKey: string;
            displayName?: string | null;
            isEnabled: boolean;
          }>;
          socketRooms: string[];
          org?: Record<string, unknown> | null;
          tenant?: Record<string, unknown> | null;
          theme?: Record<string, unknown> | null;
          currentRole?: Record<string, unknown> | null;
          user?: Record<string, unknown> | null;
          orgs: { orgId: string; name?: string | null; membershipId: string }[];
          pendingTaskCount: number;
          operationsLocked: boolean;
        };
      },
      { refreshToken: string }
    >({
      query: ({ refreshToken }) => ({
        document: `
          mutation RefreshSession($refreshToken: String!) {
            refreshSession(refreshToken: $refreshToken) {
              accessToken
              refreshToken
              featureKeys
              orgFeatures { featureKey displayName isEnabled }
              socketRooms
              org
              tenant
              theme
              currentRole
              user
              orgs {
                orgId
                name
                membershipId
              }
              pendingTaskCount
              operationsLocked
            }
          }
        `,
        variables: { refreshToken },
      }),
    }),
    listCustomers: build.query<
      CustomerListResult,
      { page?: number; pageLimit?: number; search?: string }
    >({
      query: (args) => ({
        document: `
          query ListCustomers($page: Int, $pageLimit: Int, $search: String) {
            listCustomers(page: $page, pageLimit: $pageLimit, search: $search) {
              rows { id name phone email ${ADDRESS_SELECTION} openingBalance approvalStatus approvedById approvedAt }
              ${PAGINATION_FIELDS}
            }
          }
        `,
        variables: args,
      }),
      providesTags: ['Customer'],
    }),
    getCustomer: build.query<{ getCustomer: Customer }, string>({
      query: (id) => ({
        document: `
          query GetCustomer($id: ID!) {
            getCustomer(id: $id) {
              id name phone email ${ADDRESS_SELECTION} openingBalance approvalStatus approvedById approvedAt
            }
          }
        `,
        variables: { id },
      }),
      providesTags: (_result, _error, id) => [{ type: 'Customer', id }],
    }),
    addUpdateCustomer: build.mutation<{ addUpdateCustomer: Customer }, Record<string, unknown>>({
      query: (input) => ({
        document: `
          mutation AddUpdateCustomer($input: JSON!) {
            addUpdateCustomer(input: $input) {
              id name phone email ${ADDRESS_SELECTION} openingBalance approvalStatus approvedById approvedAt
            }
          }
        `,
        variables: { input },
      }),
    }),
    deleteCustomer: build.mutation<{ deleteCustomer: Record<string, unknown> }, string>({
      query: (customerId) => ({
        document: `
          mutation DeleteCustomer($customerId: ID!) {
            deleteCustomer(customerId: $customerId)
          }
        `,
        variables: { customerId },
      }),
    }),
    customerComments: build.query<{ customerComments: ModuleComment[] }, string>({
      query: (customerId) => ({
        document: `
          query CustomerComments($customerId: ID!) {
            customerComments(customerId: $customerId) {
              id type content createdAt createdByUser
            }
          }
        `,
        variables: { customerId },
      }),
    }),
    addCustomerComment: build.mutation<
      { addCustomerComment: ModuleComment },
      { customerId: string; content: string }
    >({
      query: ({ customerId, content }) => ({
        document: `
          mutation AddCustomerComment($customerId: ID!, $content: String!) {
            addCustomerComment(customerId: $customerId, content: $content) {
              id type content createdAt createdByUser
            }
          }
        `,
        variables: { customerId, content },
      }),
    }),
    listProcurement: build.query<
      ProcListResult,
      {
        listType?: string;
        page?: number;
        pageLimit?: number;
        customerId?: string;
        vendorId?: string;
        productId?: string;
        customerIds?: string[];
        vendorIds?: string[];
        productIds?: string[];
      }
    >({
      query: (args) => {
        const variables: Record<string, unknown> = {
          listType: args.listType,
          page: args.page,
          pageLimit: args.pageLimit,
        };
        if (args.customerIds?.length) variables.customerIds = args.customerIds;
        if (args.vendorIds?.length) variables.vendorIds = args.vendorIds;
        if (args.productIds?.length) variables.productIds = args.productIds;
        return {
          document: `
          query ListProcurement(
            $listType: String
            $page: Int
            $pageLimit: Int
            $customerIds: [ID!]
            $vendorIds: [ID!]
            $productIds: [ID!]
          ) {
            listProcurement(
              listType: $listType
              page: $page
              pageLimit: $pageLimit
              customerIds: $customerIds
              vendorIds: $vendorIds
              productIds: $productIds
            ) {
              rows { ${PROC_SELECTION} }
              ${PAGINATION_FIELDS}
            }
          }
        `,
          variables,
        };
      },
      serializeQueryArgs: ({ queryArgs }) =>
        JSON.stringify({
          listType: queryArgs.listType,
          page: queryArgs.page,
          pageLimit: queryArgs.pageLimit,
          customerIds: [...(queryArgs.customerIds ?? [])].sort(),
          vendorIds: [...(queryArgs.vendorIds ?? [])].sort(),
          productIds: [...(queryArgs.productIds ?? [])].sort(),
        }),
      providesTags: ['Procurement'],
    }),
    getProcurement: build.query<{ getProcurement: ProcRow }, string>({
      query: (id) => ({
        document: `
          query GetProcurement($id: ID!) {
            getProcurement(id: $id) { ${PROC_SELECTION} }
          }
        `,
        variables: { id },
      }),
      providesTags: (_result, _error, id) => [{ type: 'Procurement', id }],
    }),
    addUpdateProcurement: build.mutation<{ addUpdateProcurement: ProcRow }, Record<string, unknown>>({
      query: (input) => ({
        document: `
          mutation AddUpdateProcurement($input: JSON!) {
            addUpdateProcurement(input: $input) {
              id procType ordNumber procAmount procUrl customerId vendorId warehouseId
            }
          }
        `,
        variables: { input },
      }),
    }),
    exportProcurement: build.mutation<{ exportProcurement: { jobId: string } }, Record<string, unknown>>({
      query: (filters) => ({
        document: `
          mutation ExportProcurement($filters: JSON) {
            exportProcurement(filters: $filters) { jobId }
          }
        `,
        variables: { filters },
      }),
    }),
    approveSalesOrder: build.mutation<
      { approveSalesOrder: { id: string; approvalStatus: string } },
      { procId: string; decision: string }
    >({
      query: ({ procId, decision }) => ({
        document: `
          mutation ApproveSalesOrder($procId: ID!, $decision: String!) {
            approveSalesOrder(procId: $procId, decision: $decision) {
              id approvalStatus approvedAt approvedById
            }
          }
        `,
        variables: { procId, decision },
      }),
    }),
    listChambers: build.query<
      { listChambers: { rows: Array<{ id: string; name: string }>; total: number; page: number; pageLimit: number } },
      { page?: number; pageLimit?: number; search?: string }
    >({
      query: (args) => ({
        document: `
          query ListChambers($page: Int, $pageLimit: Int, $search: String) {
            listChambers(page: $page, pageLimit: $pageLimit, search: $search) {
              rows { id name }
              ${PAGINATION_FIELDS}
            }
          }
        `,
        variables: args,
      }),
      providesTags: ['Chamber'],
    }),
    addUpdateChamber: build.mutation<{ addUpdateChamber: { id: string; name: string } }, Record<string, unknown>>({
      query: (input) => ({
        document: `
          mutation AddUpdateChamber($input: JSON!) {
            addUpdateChamber(input: $input) { id name }
          }
        `,
        variables: { input },
      }),
    }),
    deleteChamber: build.mutation<{ deleteChamber: unknown }, { id: string }>({
      query: ({ id }) => ({
        document: `mutation DeleteChamber($id: ID!) { deleteChamber(id: $id) }`,
        variables: { id },
      }),
    }),
    listFloors: build.query<
      {
        listFloors: {
          rows: Array<{ id: string; name: string; chamberId: string }>;
          total: number;
          page: number;
          pageLimit: number;
        };
      },
      { page?: number; pageLimit?: number; search?: string; chamberId?: string }
    >({
      query: (args) => ({
        document: `
          query ListFloors($page: Int, $pageLimit: Int, $search: String, $chamberId: ID) {
            listFloors(page: $page, pageLimit: $pageLimit, search: $search, chamberId: $chamberId) {
              rows { id name chamberId }
              ${PAGINATION_FIELDS}
            }
          }
        `,
        variables: args,
      }),
      providesTags: ['Floor'],
    }),
    addUpdateFloor: build.mutation<
      { addUpdateFloor: { id: string; name: string; chamberId: string } },
      Record<string, unknown>
    >({
      query: (input) => ({
        document: `
          mutation AddUpdateFloor($input: JSON!) {
            addUpdateFloor(input: $input) { id name chamberId }
          }
        `,
        variables: { input },
      }),
    }),
    deleteFloor: build.mutation<{ deleteFloor: unknown }, { id: string }>({
      query: ({ id }) => ({
        document: `mutation DeleteFloor($id: ID!) { deleteFloor(id: $id) }`,
        variables: { id },
      }),
    }),
    listRacks: build.query<
      {
        listRacks: {
          rows: Array<{ id: string; name: string; floorId: string }>;
          total: number;
          page: number;
          pageLimit: number;
        };
      },
      { page?: number; pageLimit?: number; search?: string; floorId?: string }
    >({
      query: (args) => ({
        document: `
          query ListRacks($page: Int, $pageLimit: Int, $search: String, $floorId: ID) {
            listRacks(page: $page, pageLimit: $pageLimit, search: $search, floorId: $floorId) {
              rows { id name floorId }
              ${PAGINATION_FIELDS}
            }
          }
        `,
        variables: args,
      }),
      providesTags: ['Rack'],
    }),
    addUpdateRack: build.mutation<
      { addUpdateRack: { id: string; name: string; floorId: string } },
      Record<string, unknown>
    >({
      query: (input) => ({
        document: `
          mutation AddUpdateRack($input: JSON!) {
            addUpdateRack(input: $input) { id name floorId }
          }
        `,
        variables: { input },
      }),
    }),
    deleteRack: build.mutation<{ deleteRack: unknown }, { id: string }>({
      query: ({ id }) => ({
        document: `mutation DeleteRack($id: ID!) { deleteRack(id: $id) }`,
        variables: { id },
      }),
    }),
    getProductStockAtRack: build.query<
      { getProductStockAtRack: number },
      { productId: string; rackId?: string | null }
    >({
      query: ({ productId, rackId }) => ({
        document: `
          query GetProductStockAtRack($productId: ID!, $rackId: ID) {
            getProductStockAtRack(productId: $productId, rackId: $rackId)
          }
        `,
        variables: { productId, rackId: rackId ?? null },
      }),
    }),
    fileUrl: build.query<{ fileUrl: { url: string; expiresAt: string } }, string>({
      query: (key) => ({
        document: `
          query FileUrl($key: String!) {
            fileUrl(key: $key) { url expiresAt }
          }
        `,
        variables: { key },
      }),
    }),
    dashboardStats: build.query<DashboardStats, void>({
      async queryFn(_arg, _api, _extra, baseQuery) {
        const result = await baseQuery({
          document: `
            query DashboardCounts {
              dashboardCounts {
                customers vendors products sales purchases
              }
            }
          `,
        });
        if (result.error) return { error: result.error };
        const counts = (result.data as {
          dashboardCounts: {
            customers: number;
            vendors: number;
            products: number;
            sales: number;
            purchases: number;
          };
        }).dashboardCounts;
        return {
          data: {
            customers: counts.customers,
            vendors: counts.vendors,
            products: counts.products,
            salesOrders: counts.sales,
            purchaseOrders: counts.purchases,
            totalProcurement: counts.sales + counts.purchases,
          },
        };
      },
      providesTags: ['Customer', 'Procurement'],
    }),
    listVendors: build.query<
      {
        listVendors: {
          rows: Array<{
            id: string;
            name: string;
            phone?: string;
            email?: string;
            address?: Address | null;
          }>;
          total: number;
        };
      },
      { page?: number; pageLimit?: number; search?: string }
    >({
      query: (args) => ({
        document: `
          query ListVendors($page: Int, $pageLimit: Int, $search: String) {
            listVendors(page: $page, pageLimit: $pageLimit, search: $search) {
              rows { id name phone email ${ADDRESS_SELECTION} approvalStatus approvedById approvedAt }
              ${PAGINATION_FIELDS}
            }
          }
        `,
        variables: args,
      }),
      providesTags: ['Vendor'],
    }),
    getVendor: build.query<{ getVendor: Vendor }, string>({
      query: (id) => ({
        document: `
          query GetVendor($id: ID!) {
            getVendor(id: $id) {
              id name phone email openingBalance ${ADDRESS_SELECTION} approvalStatus approvedById approvedAt
            }
          }
        `,
        variables: { id },
      }),
      providesTags: (_result, _error, id) => [{ type: 'Vendor', id }],
    }),
    addUpdateVendor: build.mutation<{ addUpdateVendor: { id: string } }, Record<string, unknown>>({
      query: (input) => ({
        document: `
          mutation AddUpdateVendor($input: JSON!) {
            addUpdateVendor(input: $input) { id name phone email }
          }
        `,
        variables: { input },
      }),
    }),
    deleteVendor: build.mutation<{ deleteVendor: { success: boolean } }, string>({
      query: (id) => ({
        document: `mutation DeleteVendor($id: ID!) { deleteVendor(id: $id) }`,
        variables: { id },
      }),
    }),
    listProducts: build.query<
      {
        listProducts: {
          rows: Product[];
          total: number;
          page: number;
          pageLimit: number;
        };
      },
      { page?: number; pageLimit?: number; search?: string }
    >({
      query: (args) => ({
        document: `
          query ListProducts($page: Int, $pageLimit: Int, $search: String) {
            listProducts(page: $page, pageLimit: $pageLimit, search: $search) {
              rows { ${PRODUCT_SELECTION} } ${PAGINATION_FIELDS}
            }
          }
        `,
        variables: args,
      }),
      providesTags: ['Product'],
    }),
    getProduct: build.query<{ getProduct: Product }, string>({
      query: (id) => ({
        document: `
          query GetProduct($id: ID!) {
            getProduct(id: $id) {
              ${PRODUCT_DETAIL_SELECTION}
            }
          }
        `,
        variables: { id },
      }),
      providesTags: (_result, _error, id) => [{ type: 'Product', id }],
    }),
    addUpdateProduct: build.mutation<{ addUpdateProduct: Product }, Record<string, unknown>>({
      query: (input) => ({
        document: `
          mutation AddUpdateProduct($input: JSON!) {
            addUpdateProduct(input: $input) { ${PRODUCT_DETAIL_SELECTION} }
          }
        `,
        variables: { input },
      }),
    }),
    deleteProduct: build.mutation<{ deleteProduct: { success: boolean } }, string>({
      query: (id) => ({
        document: `mutation DeleteProduct($id: ID!) { deleteProduct(id: $id) }`,
        variables: { id },
      }),
    }),
    listCategories: build.query<
      {
        listCategories: {
          rows: Array<{ id: string; name: string }>;
          total: number;
          page: number;
          pageLimit: number;
        };
      },
      { page?: number; pageLimit?: number; search?: string }
    >({
      query: (args) => ({
        document: `
          query ListCategories($page: Int, $pageLimit: Int, $search: String) {
            listCategories(page: $page, pageLimit: $pageLimit, search: $search) {
              rows { id name } ${PAGINATION_FIELDS}
            }
          }
        `,
        variables: args,
      }),
      providesTags: ['Product'],
    }),
    addUpdateCategory: build.mutation<
      { addUpdateCategory: { id: string; name: string } },
      Record<string, unknown>
    >({
      query: (input) => ({
        document: `
          mutation AddUpdateCategory($input: JSON!) {
            addUpdateCategory(input: $input) { id name }
          }
        `,
        variables: { input },
      }),
      invalidatesTags: ['Product'],
    }),
    deleteCategory: build.mutation<{ deleteCategory: unknown }, { id: string }>({
      query: ({ id }) => ({
        document: `mutation DeleteCategory($id: ID!) { deleteCategory(id: $id) }`,
        variables: { id },
      }),
      invalidatesTags: ['Product'],
    }),
    listUnits: build.query<
      {
        listUnits: {
          rows: Array<{ id: string; name: string; shortName?: string | null }>;
          total: number;
          page: number;
          pageLimit: number;
        };
      },
      { page?: number; pageLimit?: number; search?: string }
    >({
      query: (args) => ({
        document: `
          query ListUnits($page: Int, $pageLimit: Int, $search: String) {
            listUnits(page: $page, pageLimit: $pageLimit, search: $search) {
              rows { id name shortName } ${PAGINATION_FIELDS}
            }
          }
        `,
        variables: args,
      }),
      providesTags: ['Product'],
    }),
    addUpdateUnit: build.mutation<
      { addUpdateUnit: { id: string; name: string } },
      Record<string, unknown>
    >({
      query: (input) => ({
        document: `
          mutation AddUpdateUnit($input: JSON!) {
            addUpdateUnit(input: $input) { id name shortName }
          }
        `,
        variables: { input },
      }),
      invalidatesTags: ['Product'],
    }),
    deleteUnit: build.mutation<{ deleteUnit: unknown }, { id: string }>({
      query: ({ id }) => ({
        document: `mutation DeleteUnit($id: ID!) { deleteUnit(id: $id) }`,
        variables: { id },
      }),
      invalidatesTags: ['Product'],
    }),
    listStaff: build.query<
      {
        listStaff: {
          rows: Array<{
            id: string;
            userId: string;
            roleId?: string | null;
            isActive: boolean;
            user?: { id?: string; name?: string; phone?: string; email?: string } | null;
            role?: { id?: string; name?: string } | null;
          }>;
          total: number;
          page: number;
          pageLimit: number;
        };
      },
      { page?: number; pageLimit?: number; search?: string }
    >({
      query: (args) => ({
        document: `
          query ListStaff($page: Int, $pageLimit: Int, $search: String) {
            listStaff(page: $page, pageLimit: $pageLimit, search: $search) {
              rows { id userId roleId isActive warehouseIds user role }
              ${PAGINATION_FIELDS}
            }
          }
        `,
        variables: args,
      }),
      providesTags: ['Staff'],
    }),
    addUpdateStaff: build.mutation<{ addUpdateStaff: { id: string } }, Record<string, unknown>>({
      query: (input) => ({
        document: `
          mutation AddUpdateStaff($input: JSON!) {
            addUpdateStaff(input: $input) { id userId roleId isActive user role }
          }
        `,
        variables: { input },
      }),
    }),
    deleteStaff: build.mutation<{ deleteStaff: { success: boolean } }, string>({
      query: (id) => ({
        document: `mutation DeleteStaff($id: ID!) { deleteStaff(id: $id) }`,
        variables: { id },
      }),
    }),
    listRoles: build.query<
      { listRoles: Array<{ id: string; name: string; featureKeys: string[] }> },
      void
    >({
      query: () => ({
        document: `
          query ListRoles {
            listRoles { id name featureKeys }
          }
        `,
      }),
      providesTags: ['Staff'],
    }),
    listFeatureGroups: build.query<
      { listFeatureGroups: Array<{ module: string; label: string; keys: string[] }> },
      void
    >({
      query: () => ({
        document: `
          query ListFeatureGroups {
            listFeatureGroups { module label keys }
          }
        `,
      }),
      providesTags: ['Staff'],
    }),
    listApprovalFlows: build.query<
      {
        listApprovalFlows: Array<{
          id: string;
          orgId: string;
          subjectType: string;
          subjectId: string;
          templateCode: string;
          status: string;
          createdById?: string | null;
          createdAt?: string | null;
          steps?: Array<{
            id: string;
            approvalFlowId: string;
            stepOrder: number;
            stepKind: string;
            assigneeMembershipId: string;
            status: string;
            decidedByUserId?: string | null;
            decidedAt?: string | null;
            note?: string | null;
            taskInstanceId?: string | null;
          }> | null;
        }>;
      },
      { subjectType: string; subjectId: string }
    >({
      query: (args) => ({
        document: `
          query ListApprovalFlows($subjectType: String!, $subjectId: ID!) {
            listApprovalFlows(subjectType: $subjectType, subjectId: $subjectId) {
              id orgId subjectType subjectId templateCode status createdById createdAt
              steps {
                id approvalFlowId stepOrder stepKind assigneeMembershipId status
                decidedByUserId decidedAt note taskInstanceId
              }
            }
          }
        `,
        variables: args,
      }),
      providesTags: (_r, _e, arg) => {
        const subject = String(arg.subjectType ?? '').toUpperCase();
        const id = `${arg.subjectType}-${arg.subjectId}`;
        const tagBySubject: Record<string, 'Customer' | 'Vendor' | 'Product' | 'Procurement' | 'Staff' | 'Task'> = {
          CUSTOMER: 'Customer',
          VENDOR: 'Vendor',
          PRODUCT: 'Product',
          PROC: 'Procurement',
          PROCUREMENT: 'Procurement',
          PROC_SALES: 'Procurement',
          PROC_PURCHASE: 'Procurement',
        };
        const type = tagBySubject[subject] ?? 'Task';
        return [{ type, id }, 'Task', 'Staff'];
      },
    }),
    approveEntity: build.mutation<
      { approveEntity: { ok: boolean; flowId: string; status: string } },
      { subjectType: string; subjectId: string; decision: string; note?: string }
    >({
      query: (args) => ({
        document: `
          mutation ApproveEntity($subjectType: String!, $subjectId: ID!, $decision: String!, $note: String) {
            approveEntity(subjectType: $subjectType, subjectId: $subjectId, decision: $decision, note: $note)
          }
        `,
        variables: args,
      }),
      // Prefer socket subject-row patch; silent invalidate only as local fallback.
      invalidatesTags: ['Task', 'Staff'],
    }),
    listApprovalFlowDefinitions: build.query<
      {
        listApprovalFlowDefinitions: Array<{
          id: string;
          orgId: string;
          moduleKey: string;
          isEnabled: boolean;
          levels?: Array<{
            id: string;
            levelOrder: number;
            roleId: string;
            roleName?: string | null;
            minAmount: number;
          }> | null;
        }>;
      },
      void
    >({
      query: () => ({
        document: `
          query ListApprovalFlowDefinitions {
            listApprovalFlowDefinitions {
              id orgId moduleKey isEnabled
              levels { id levelOrder roleId roleName minAmount }
            }
          }
        `,
      }),
      providesTags: ['Staff'],
    }),
    upsertApprovalFlowDefinition: build.mutation<
      {
        upsertApprovalFlowDefinition: {
          id: string;
          orgId: string;
          moduleKey: string;
          isEnabled: boolean;
          levels?: Array<{
            id: string;
            levelOrder: number;
            roleId: string;
            roleName?: string | null;
            minAmount: number;
          }> | null;
        };
      },
      {
        moduleKey: string;
        isEnabled: boolean;
        levels: Array<{ levelOrder: number; roleId: string; minAmount?: number }>;
      }
    >({
      query: (input) => ({
        document: `
          mutation UpsertApprovalFlowDefinition($input: JSON!) {
            upsertApprovalFlowDefinition(input: $input) {
              id orgId moduleKey isEnabled
              levels { id levelOrder roleId roleName minAmount }
            }
          }
        `,
        variables: { input },
      }),
      invalidatesTags: ['Staff'],
    }),
    myOrgBilling: build.query<
      {
        myOrgBilling: {
          planName: string;
          planSlug: string;
          status: string;
          billingPeriod: string;
          validFrom?: string | null;
          validUntil?: string | null;
          seatsUsed: number;
          seatsAllowed: number;
          seatCeiling: number;
          baseMonthly: number;
          baseYearly: number;
          taxPct: number;
          extraSeatMonthly: number;
          extraSeatYearly: number;
          operationsLocked: boolean;
          paymentDueAt?: string | null;
          activeAddOns: string[];
          addOnCatalogue: Array<{
            key: string;
            name: string;
            description?: string | null;
            meter: string;
            monthlyPrice: number;
            yearlyPrice: number;
            isActive: boolean;
            isSubscribed: boolean;
          }>;
        };
      },
      void
    >({
      query: () => ({
        document: `
          query MyOrgBilling {
            myOrgBilling {
              planName planSlug status billingPeriod validFrom validUntil
              seatsUsed seatsAllowed seatCeiling baseMonthly baseYearly taxPct
              extraSeatMonthly extraSeatYearly
              operationsLocked paymentDueAt activeAddOns
              addOnCatalogue {
                key name description meter monthlyPrice yearlyPrice isActive isSubscribed
              }
            }
          }
        `,
      }),
      providesTags: ['Billing'],
    }),
    myOrgInvoices: build.query<
      {
        myOrgInvoices: Array<{
          id: string;
          invoiceNumber: string;
          status: string;
          subtotal: number;
          total: number;
          taxPct: number;
          taxTotal: number;
          grandTotal: number;
          currency: string;
          issuedAt: string;
          dueAt?: string | null;
          paidAt?: string | null;
          markedPaidByAdminId?: string | null;
          notes?: string | null;
          pdfFileId?: string | null;
        }>;
      },
      void
    >({
      query: () => ({
        document: `
          query MyOrgInvoices {
            myOrgInvoices {
              id invoiceNumber status subtotal total taxPct taxTotal grandTotal
              currency issuedAt dueAt paidAt markedPaidByAdminId notes pdfFileId
            }
          }
        `,
      }),
      providesTags: ['Billing'],
    }),
    createRazorpayOrder: build.mutation<
      {
        createRazorpayOrder: {
          orderId: string;
          keyId: string;
          amount: number;
          currency: string;
          invoiceId: string;
          invoiceNumber: string;
          name: string;
          description: string;
        };
      },
      { invoiceId: string }
    >({
      query: ({ invoiceId }) => ({
        document: `
          mutation CreateRazorpayOrder($invoiceId: ID!) {
            createRazorpayOrder(invoiceId: $invoiceId) {
              orderId keyId amount currency invoiceId invoiceNumber name description
            }
          }
        `,
        variables: { invoiceId },
      }),
      invalidatesTags: ['Staff'],
    }),
    verifyRazorpayPayment: build.mutation<
      {
        verifyRazorpayPayment: {
          id: string;
          invoiceNumber: string;
          status: string;
          paidAt?: string | null;
          pdfFileId?: string | null;
        };
      },
      {
        invoiceId: string;
        razorpayPaymentId: string;
        razorpayOrderId: string;
        razorpaySignature: string;
      }
    >({
      query: (vars) => ({
        document: `
          mutation VerifyRazorpayPayment(
            $invoiceId: ID!
            $razorpayPaymentId: String!
            $razorpayOrderId: String!
            $razorpaySignature: String!
          ) {
            verifyRazorpayPayment(
              invoiceId: $invoiceId
              razorpayPaymentId: $razorpayPaymentId
              razorpayOrderId: $razorpayOrderId
              razorpaySignature: $razorpaySignature
            ) {
              id invoiceNumber status paidAt pdfFileId
            }
          }
        `,
        variables: vars,
      }),
      invalidatesTags: ['Staff'],
    }),
    myTasks: build.query<
      {
        myTasks: Array<{
          id: string;
          templateId: string;
          orgId: string;
          assigneeType: string;
          assigneeId: string;
          status: string;
          priority: string;
          subjectType?: string | null;
          subjectId?: string | null;
          template?: {
            id: string;
            code: string;
            title: string;
            description?: string | null;
            category: string;
            kind: string;
            actionKey: string;
            priority: string;
          } | null;
        }>;
      },
      { status?: string; category?: string } | void
    >({
      query: (variables) => ({
        document: `
          query MyTasks($status: String, $category: String) {
            myTasks(status: $status, category: $category) {
              id templateId orgId assigneeType assigneeId status priority
              subjectType subjectId
              template {
                id code title description category kind actionKey priority
              }
            }
          }
        `,
        variables: variables ?? {},
      }),
      providesTags: ['Task'],
    }),
    completeTask: build.mutation<
      {
        completeTask: {
          id: string;
          status: string;
        };
      },
      { instanceId: string; input: Record<string, unknown> }
    >({
      query: ({ instanceId, input }) => ({
        document: `
          mutation CompleteTask($instanceId: ID!, $input: JSON!) {
            completeTask(instanceId: $instanceId, input: $input) {
              id status
            }
          }
        `,
        variables: { instanceId, input },
      }),
    }),
    resolveTask: build.mutation<
      { resolveTask: { id: string; status: string } },
      { instanceId: string; decision: 'APPROVED' | 'REJECTED'; note?: string }
    >({
      query: (variables) => ({
        document: `
          mutation ResolveTask($instanceId: ID!, $decision: String!, $note: String) {
            resolveTask(instanceId: $instanceId, decision: $decision, note: $note) {
              id status
            }
          }
        `,
        variables,
      }),
    }),
    listOrgSettings: build.query<
      {
        listOrgSettings: Array<{
          id: string;
          code: string;
          valueType: string;
          stringValue?: string | null;
          booleanValue?: boolean | null;
          numberValue?: number | null;
          arrayValue?: unknown[] | null;
          enumOptions?: string[] | null;
        }>;
      },
      void
    >({
      query: () => ({
        document: `
          query ListOrgSettings {
            listOrgSettings {
              id code valueType stringValue booleanValue numberValue arrayValue enumOptions
            }
          }
        `,
      }),
      providesTags: ['Tenant'],
    }),
    updateOrgLogo: build.mutation<
      { updateOrgLogo: { logoFileId?: string | null; logoUrl?: string | null } },
      { fileId?: string | null }
    >({
      query: ({ fileId }) => ({
        document: `
          mutation UpdateOrgLogo($fileId: ID) {
            updateOrgLogo(fileId: $fileId)
          }
        `,
        variables: { fileId: fileId ?? null },
      }),
      invalidatesTags: ['Tenant'],
    }),
    updateOrgProfile: build.mutation<
      {
        updateOrgProfile: {
          id: string;
          name: string;
          phone?: string | null;
          businessEmail?: string | null;
          contactPersonPhone?: string | null;
          gstin?: string | null;
          address?: Address | null;
        };
      },
      Record<string, unknown>
    >({
      query: (input) => ({
        document: `
          mutation UpdateOrgProfile($input: JSON!) {
            updateOrgProfile(input: $input) {
              id
              name
              phone
              businessEmail
              contactPersonPhone
              gstin
              ${ADDRESS_SELECTION}
            }
          }
        `,
        variables: { input },
      }),
      invalidatesTags: ['Tenant'],
    }),
    updateMyProfile: build.mutation<
      {
        updateMyProfile: {
          id: string;
          name: string;
          phone: string;
          email?: string | null;
          address?: Address | null;
        };
      },
      Record<string, unknown>
    >({
      query: (input) => ({
        document: `
          mutation UpdateMyProfile($input: JSON!) {
            updateMyProfile(input: $input) {
              id
              name
              phone
              email
              ${ADDRESS_SELECTION}
            }
          }
        `,
        variables: { input },
      }),
    }),
    addUpdateRole: build.mutation<
      { addUpdateRole: { id: string; name: string; featureKeys: string[] } },
      Record<string, unknown>
    >({
      query: (input) => ({
        document: `
          mutation AddUpdateRole($input: JSON!) {
            addUpdateRole(input: $input) { id name featureKeys }
          }
        `,
        variables: { input },
      }),
      invalidatesTags: ['Staff'],
    }),
    deleteRole: build.mutation<{ deleteRole: unknown }, string>({
      query: (id) => ({
        document: `mutation DeleteRole($id: ID!) { deleteRole(id: $id) }`,
        variables: { id },
      }),
      invalidatesTags: ['Staff'],
    }),
    getProductInventoryCounts: build.query<
      { getProductInventoryCounts: { inHouse: number; purchasePool: number; inApproval: number } },
      { productId: string | number }
    >({
      query: ({ productId }) => ({
        document: `
          query GetProductInventoryCounts($productId: ID!) {
            getProductInventoryCounts(productId: $productId) {
              inHouse purchasePool inApproval
            }
          }
        `,
        variables: { productId: String(productId) },
      }),
      providesTags: ['InventoryTransfer'],
    }),
    listInventoryTransfers: build.query<
      { listInventoryTransfers: { rows: InventoryTransferRow[]; total: number; page: number; pageLimit: number } },
      { productId?: string | number; warehouseId?: string | number; page?: number; pageLimit?: number }
    >({
      query: (args) => ({
        document: `
          query ListInventoryTransfers($productId: ID, $warehouseId: ID, $page: Int, $pageLimit: Int) {
            listInventoryTransfers(productId: $productId, warehouseId: $warehouseId, page: $page, pageLimit: $pageLimit) {
              rows {
                id orgId productId fromType fromRackId toType toRackId warehouseId
                quantity lossPercent lossKg notes status createdAt
                product { id name unit }
                fromRack { id name }
                toRack { id name }
              }
              total page pageLimit
            }
          }
        `,
        variables: args,
      }),
      providesTags: ['InventoryTransfer'],
    }),
    addUpdateInventoryTransfer: build.mutation<
      { addUpdateInventoryTransfer: InventoryTransferRow },
      Record<string, unknown>
    >({
      query: (input) => ({
        document: `
          mutation AddUpdateInventoryTransfer($input: JSON!) {
            addUpdateInventoryTransfer(input: $input) {
              id orgId productId fromType fromRackId toType toRackId warehouseId
              quantity lossPercent lossKg notes status createdAt
            }
          }
        `,
        variables: { input },
      }),
      invalidatesTags: ['InventoryTransfer', 'Product'],
    }),
    confirmInventoryTransfer: build.mutation<
      { confirmInventoryTransfer: { id: string; status: string } },
      string | number
    >({
      query: (id) => ({
        document: `
          mutation ConfirmInventoryTransfer($id: ID!) {
            confirmInventoryTransfer(id: $id) { id status }
          }
        `,
        variables: { id: String(id) },
      }),
      invalidatesTags: ['InventoryTransfer', 'Product'],
    }),
    deleteInventoryTransfer: build.mutation<{ deleteInventoryTransfer: boolean }, string | number>({
      query: (id) => ({
        document: `
          mutation DeleteInventoryTransfer($id: ID!) {
            deleteInventoryTransfer(id: $id)
          }
        `,
        variables: { id: String(id) },
      }),
      invalidatesTags: ['InventoryTransfer', 'Product'],
    }),
    upsertOrgSetting: build.mutation<
      {
        upsertOrgSetting: {
          id: string;
          code: string;
          valueType: string;
          stringValue?: string | null;
          booleanValue?: boolean | null;
          numberValue?: number | null;
          arrayValue?: unknown[] | null;
          enumOptions?: string[] | null;
        };
      },
      {
        code: string;
        valueType: string;
        stringValue?: string | null;
        booleanValue?: boolean | null;
        numberValue?: number | null;
        arrayValue?: unknown[] | null;
        enumOptions?: string[] | null;
      }
    >({
      query: (variables) => ({
        document: `
          mutation UpsertOrgSetting(
            $code: String!
            $valueType: String!
            $stringValue: String
            $booleanValue: Boolean
            $numberValue: Float
            $arrayValue: JSON
            $enumOptions: [String!]
          ) {
            upsertOrgSetting(
              code: $code
              valueType: $valueType
              stringValue: $stringValue
              booleanValue: $booleanValue
              numberValue: $numberValue
              arrayValue: $arrayValue
              enumOptions: $enumOptions
            ) {
              id code valueType stringValue booleanValue numberValue arrayValue enumOptions
            }
          }
        `,
        variables,
      }),
      invalidatesTags: ['Tenant'],
    }),
  }),
});

export const {
  useTenantBySlugQuery,
  useLoginMutation,
  useLoginWithPasswordMutation,
  useVerifyOtpMutation,
  useInitAppMutation,
  useRefreshSessionMutation,
  useListCustomersQuery,
  useGetCustomerQuery,
  useLazyGetCustomerQuery,
  useLazyListCustomersQuery,
  useAddUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useCustomerCommentsQuery,
  useAddCustomerCommentMutation,
  useListProcurementQuery,
  useGetProcurementQuery,
  useAddUpdateProcurementMutation,
  useExportProcurementMutation,
  useApproveSalesOrderMutation,
  useListChambersQuery,
  useAddUpdateChamberMutation,
  useDeleteChamberMutation,
  useListFloorsQuery,
  useLazyListFloorsQuery,
  useAddUpdateFloorMutation,
  useDeleteFloorMutation,
  useListRacksQuery,
  useLazyListRacksQuery,
  useAddUpdateRackMutation,
  useDeleteRackMutation,
  useGetProductStockAtRackQuery,
  useLazyGetProductStockAtRackQuery,
  useFileUrlQuery,
  useLazyFileUrlQuery,
  useDashboardStatsQuery,
  useListVendorsQuery,
  useGetVendorQuery,
  useLazyGetVendorQuery,
  useLazyListVendorsQuery,
  useAddUpdateVendorMutation,
  useDeleteVendorMutation,
  useListProductsQuery,
  useGetProductQuery,
  useLazyGetProductQuery,
  useLazyListProductsQuery,
  useAddUpdateProductMutation,
  useDeleteProductMutation,
  useLazyListCategoriesQuery,
  useListCategoriesQuery,
  useAddUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useLazyListUnitsQuery,
  useListUnitsQuery,
  useAddUpdateUnitMutation,
  useDeleteUnitMutation,
  useListStaffQuery,
  useAddUpdateStaffMutation,
  useDeleteStaffMutation,
  useListRolesQuery,
  useListFeatureGroupsQuery,
  useListApprovalFlowsQuery,
  useApproveEntityMutation,
  useListApprovalFlowDefinitionsQuery,
  useUpsertApprovalFlowDefinitionMutation,
  useMyOrgBillingQuery,
  useMyOrgInvoicesQuery,
  useCreateRazorpayOrderMutation,
  useVerifyRazorpayPaymentMutation,
  useMyTasksQuery,
  useCompleteTaskMutation,
  useResolveTaskMutation,
  useListOrgSettingsQuery,
  useUpdateOrgLogoMutation,
  useUpdateOrgProfileMutation,
  useUpdateMyProfileMutation,
  useUpsertOrgSettingMutation,
  useAddUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetProductInventoryCountsQuery,
  useListInventoryTransfersQuery,
  useAddUpdateInventoryTransferMutation,
  useConfirmInventoryTransferMutation,
  useDeleteInventoryTransferMutation,
} = tenantApi;
